import { Component, 
  OnInit, 
  OnDestroy,
  Output, 
  EventEmitter, 
  ChangeDetectorRef, 
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { parseAsLocalDate } from '../../../../shared/_utils/date-utils';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../../shared/workspace-element-context';
import {
  F1065K2DataEntryService,
  ColumnHeaderData,
  LineData,
  GroupedFormLine,
  FormLineItem,
  SaveDataItem,
  ClobSettingsObj,
  FilterParams
} from '../f1065-k2-data-entry.service';

@Component({
    selector: 'gtw-f1065-k2-p1s0',
    templateUrl: './f1065-k2-p1s0.component.html',
    styleUrls: ['./f1065-k2-p1s0.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1065K2P1S0Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  private lastContextKey = '';

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: any = {};
  globalService: any = {};
  filterParams: FilterParams = {};
  GENERAL_CONFIG: any = {};

  private isInitialized = false;
  private isLoading = false;

  loadingData = true;
  showLoader = true;

  colHeaderData: ColumnHeaderData[] = [];
  lineData: LineData[] = [];
  groupedFormLines: GroupedFormLine[] = [];

  totalDropDownActions = 0;
  loadedActionCounter = 0;

  isAdmin = false;
  isSaveClicked = false;
  showErrCol = false;
  isValidationError = false;

  currentCodeGroup = '';
  formSchd = '';
  currentEntityName = '';
  currentPartnerName = '';
  scenario = '';
  jcdKey = 0;

  // P1S0 specific
  sectionHeader = "Partnership's Other Current Year International Information:";
  lineDetailsActionCode = 'w5bepp';

  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Save Data',
    message: 'Are you sure to save data?',
    iconClass: 'fa fa-save',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true
  };

  constructor(
    private service: F1065K2DataEntryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwWorkspace.refreshGridData.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
      this.cdr.markForCheck();
    }));
    // Explicit initial call — tryInit() has internal dedup (lastContextKey + isLoading),
    // so redundant calls from BehaviorSubject replay are harmless.
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const generalConfig = generalConfigFromGlobal(this.gtwGlobal);
    const baseUrl = generalConfig?.base_url || generalConfig?.api || '';
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};

    this.baseURLs = { ...(this.baseURLs || {}), api: baseUrl };
    this.GENERAL_CONFIG = { ...generalConfig, base_url: baseUrl };
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.scenario = this.filterParams?.scenario || '';
    this.jcdKey = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
  }

  private tryInit(): void {
    if (this.isLoading) return;

    const hasGlobalService = !!this.globalService && Object.keys(this.globalService).length > 0;
    const hasBaseUrl = !!this.GENERAL_CONFIG?.base_url;
    const hasFilterParams = !!this.filterParams && Object.keys(this.filterParams).length > 0;

    if (!hasGlobalService || !hasBaseUrl || !hasFilterParams) return;

    const contextKey = [
      this.GENERAL_CONFIG?.base_url || '',
      this.filterParams?.scenario || '',
      this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      this.filterParams?.p_form || '',
      this.filterParams?.p_schedule || '',
      this.gtwWorkspace.workspaceFactory?.activeScreen?.link || ''
    ].join('|');

    if (contextKey === this.lastContextKey) return;

    this.lastContextKey = contextKey;

    if (this.isInitialized) {
      this.reloadData();
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    try {
      this.scenario = this.filterParams.scenario || '';
      this.jcdKey = this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
      this.checkLockStatus();
      this.setCurrentCodeGroup();
      this.loadColumnHeaders();
    } catch (e) {
      console.error('[F1065K2-P1S0] Error in init:', e);
      this.loadingData = false;
      this.showLoader = false;
      this.cdr.detectChanges();
    }
  }

  private reloadData(): void {
    this.isLoading = false;
    this.lineData = [];
    this.groupedFormLines = [];
    this.colHeaderData = [];
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;
    
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();

    this.scenario = this.filterParams.scenario || '';
    this.jcdKey = this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
    this.setCurrentCodeGroup();
    this.checkLockStatus();
    this.loadColumnHeaders();
  }

  private setCurrentCodeGroup(): void {
    const form = this.filterParams.p_form || 'F1065';
    const schedule = this.filterParams.p_schedule || 'K2';
    const linkPart = '_P1_S0';
    this.currentCodeGroup = `${form}_${schedule}${linkPart}`;
    this.formSchd = `${form}_${schedule}`;
  }

  private checkLockStatus(): void {
    if (!this.GENERAL_CONFIG?.base_url) return;

    this.service.getLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        if (data && data.length > 0 && data[0].LOCK_STATUS === 'Y') {
          this.isAdmin = false;
          this.service.showError(`Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`);
        }
        this.cdr.detectChanges();
      },
      (error) => console.error('Error checking lock status:', error)
    );
  }

  private loadColumnHeaders(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;
    this.cdr.detectChanges();

    this.service.getColumnHeaders(
      this.GENERAL_CONFIG.base_url,
      this.filterParams.tax_year || '',
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        this.colHeaderData = data || [];
        
        if (!this.colHeaderData || this.colHeaderData.length === 0) {
          this.service.showWarning('Data Error: No results were returned');
          this.loadingData = true;
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        let hasDropdowns = false;
        for (const col of this.colHeaderData) {
          this.setValidationInfo(col, col);
          if (col.DROP_DOWN_ACTION && col.DROP_DOWN_ACTION.trim() !== '') {
            hasDropdowns = true;
            this.totalDropDownActions++;
            this.loadDropdownData(col);
          }
        }

        if (!hasDropdowns) {
          this.loadLineDetails();
        }
      },
      (error) => {
        console.error('Error loading column headers:', error);
        this.service.showError('Error loading form configuration');
        this.loadingData = false;
        this.showLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadDropdownData(colData: ColumnHeaderData): void {
    const actionDetails = colData.DROP_DOWN_ACTION!.split(':');
    const actionCode = actionDetails[0];
    const codeGroup = actionDetails[1] || '';

    this.service.getDropdownData(
      this.GENERAL_CONFIG.base_url,
      actionCode,
      codeGroup,
      this.filterParams.tax_year || '',
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        colData.actionData = data || [];
        this.loadedActionCounter++;
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
        this.cdr.markForCheck();
      },
      (error) => {
        console.error('Error loading dropdown data:', error);
        this.loadedActionCounter++;
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
        this.cdr.markForCheck();
      }
    );
  }

  private loadLineDetails(): void {
    this.showLoader = true;

    const filerKey = this.filterParams.p_filer_key;
    const hasFilerKey = filerKey !== undefined && filerKey !== null && filerKey !== '' && 
                        (typeof filerKey === 'string' ? filerKey.trim().length > 0 : filerKey >= 0);
    
    if (!hasFilerKey) {
      this.service.showError('Please select all the filters');
      this.showLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const partnerKey = this.filterParams.p_filer_partner_key;
    const hasPartnerKey = partnerKey !== undefined && partnerKey !== null && partnerKey !== '' &&
                          (typeof partnerKey === 'string' ? partnerKey.trim().length > 0 : (partnerKey as number) >= 0);
    
    if (this.currentCodeGroup.includes('_K3') && !hasPartnerKey) {
      this.service.showError('Please select all the filters');
      this.showLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // Set entity and partner labels for confirmation modal
    this.currentEntityName = this.filterParams.p_filer_label || '';
    if (this.currentCodeGroup.includes('_K3')) {
      this.currentPartnerName = this.filterParams.p_filer_partner_label || '';
    } else {
      this.currentPartnerName = '';
    }

    this.service.getLineDetails(
      this.GENERAL_CONFIG.base_url,
      this.lineDetailsActionCode,
      this.filterParams.tax_year || '',
      filerKey!,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams,
      this.filterParams.p_filer_partner_key
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        this.lineData = data || [];
        this.groupedFormLines = this.processData(this.lineData);

        // P1S0: Empty calcTotalsCol - no totals
        for (const line of this.groupedFormLines) {
          this.calcTotalsCol(line.items);
        }

        this.showLoader = false;
        this.loadingData = false;
        this.showErrCol = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error loading line details:', error);
        this.service.showError('Error loading form data');
        this.showLoader = false;
        this.loadingData = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private processData(formLines: LineData[]): GroupedFormLine[] {
    if (!formLines || formLines.length === 0) return [];

    this.isAdmin = true;

    return _.reduce(
      formLines,
      (memo: GroupedFormLine[], property: LineData) => {
        let bucket = _.find(memo, {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          LINE_NO: property.LINE_NO,
          LINE_DESC: property.LINE_DESC,
          LINE_ORDER: property.LINE_ORDER,
          IS_DISPLAY: 'Y',
          SNO: property.SNO,
          IS_LINE_MULTI: property.IS_LINE_MULTI
        });

        if (!bucket) {
          bucket = {
            SCHD_KEY: property.SCHD_KEY,
            LINE_KEY: property.LINE_KEY,
            LINE_NO: property.LINE_NO,
            LINE_DESC: property.LINE_DESC,
            LINE_ORDER: property.LINE_ORDER,
            IS_DISPLAY: 'Y',
            SNO: property.SNO,
            IS_LINE_MULTI: property.IS_LINE_MULTI,
            items: []
          };
          memo.push(bucket);
        }

        const attribs: FormLineItem = {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          COL_KEY: property.COL_KEY,
          COL_DESC: property.COL_DESC,
          COL_ORDER: property.COL_ORDER,
          IS_ENABLED: property.EDITABLE_Y_N,
          SNO: property.SNO,
          ADJ_KEY: property.ADJ_KEY,
          IS_COL_MULTI: property.IS_COL_MULTI,
          UI_COMP: property.UI_COMP,
          ACTION_Y_N: property.ACTION_Y_N,
          IS_MANDATORY: 'N',
          CELL_VALUE: property.CELL_VALUE
        };

        if (attribs.ACTION_Y_N === 'Y') {
          const colObj = this.colHeaderData.find(col => col.COL_KEY === attribs.COL_KEY);
          if (colObj) {
            attribs.actionData = colObj.actionData;
          }
        }

        this.setValidationInfo(property, attribs);

        if (attribs.ADJ_KEY !== undefined && attribs.ADJ_KEY !== null) {
          attribs.OPERATION_TYPE = 'U';
        } else {
          attribs.OPERATION_TYPE = undefined;
        }

        // P1S0 specific: Different default value logic
        if (property.SNO === 0 && property.IS_LINE_MULTI === 'Y' && attribs.CELL_VALUE === null) {
          attribs.CELL_VALUE = '';
        }

        if (attribs.UI_COMP === 'DATE') {
          if (attribs.CELL_VALUE !== 0 && attribs.CELL_VALUE) {
            const parsedDate = parseAsLocalDate(property.CELL_VALUE);
            if (parsedDate) {
              attribs.CELL_VALUE = this.formatDateForInput(parsedDate);
            }
          } else {
            attribs.CELL_VALUE = '';
          }
        }

        if (this.isSkipFromSaving(bucket, attribs)) {
          attribs.OPERATION_TYPE = 'N';
        }

        bucket.items.push(attribs);
        return memo;
      },
      []
    );
  }

  // P1S0: Simple validation (no PREF_REQ)
  private setValidationInfo(source: any, dest: any): void {
    if (source.VALIDATION_RULE) {
      const rules = source.VALIDATION_RULE.split(';');
      for (const rule of rules) {
        if (rule === 'REQ') {
          dest.IS_MANDATORY = 'Y';
        } else if (rule.includes('MAX_LEN:')) {
          dest.MAX_LENGTH = parseInt(rule.split(':')[1], 10);
        }
      }
    }
  }

  private isSkipFromSaving(bucket: GroupedFormLine, attribs: FormLineItem): boolean {
    return attribs.IS_ENABLED === 'N';
  }

  private isEmptyVal(val: any): boolean {
    return val === null || val === '' || val === undefined;
  }

  private isNotEmpty(item: FormLineItem): boolean {
    return item.CELL_VALUE !== undefined && item.CELL_VALUE !== null && item.CELL_VALUE !== 0 && item.CELL_VALUE !== '';
  }

  private isEmpty(item: FormLineItem): boolean {
    return !this.isNotEmpty(item);
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format date for save - YYYY-M-D format (without leading zeros)
   * Matches original AngularJS: tempDate.getFullYear() + '-' + (tempDate.getMonth() + 1) + '-' + tempDate.getDate()
   * Handles both Date objects and string inputs
   */
  private formatDateForSave(dateValue: string | Date | null): string {
    if (!dateValue) return '';
    
    // If it's already a Date object, format directly
    if (dateValue instanceof Date) {
      return `${dateValue.getFullYear()}-${dateValue.getMonth() + 1}-${dateValue.getDate()}`;
    }
    
    // If it's a string, parse and format
    const date = parseAsLocalDate(dateValue);
    if (date) {
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    }
    
    return String(dateValue);
  }

  addRow(selectedRowData: GroupedFormLine): void {
    const index = this.groupedFormLines.indexOf(selectedRowData);
    const tempObj = _.cloneDeep(selectedRowData);
    
    const multiRow = this.groupedFormLines.filter(
      line => line.LINE_KEY === selectedRowData.LINE_KEY && line.IS_DISPLAY === 'Y'
    );
    const sno = multiRow[multiRow.length - 1].SNO + 1;

    tempObj.LINE_DESC = '';
    tempObj.LINE_NO = '';
    tempObj.ERR_MSG = undefined;
    tempObj.SNO = sno;

    // P1S0 specific: Different default values
    for (const item of tempObj.items) {
      item.OPERATION_TYPE = 'I';
      item.ADJ_KEY = null;
      if (item.UI_COMP === 'DATE') {
        item.CELL_VALUE = '';
      } else {
        item.CELL_VALUE = 0;
      }
      item.SNO = sno;
    }

    this.groupedFormLines.splice(index + sno + 1, 0, tempObj);
    this.cdr.detectChanges();
  }

  deleteRow(selectedRowData: GroupedFormLine): void {
    let isRowSaved = false;

    for (const item of selectedRowData.items) {
      if (item.OPERATION_TYPE === 'U') {
        item.OPERATION_TYPE = 'D';
        selectedRowData.IS_DISPLAY = 'N';
        isRowSaved = true;
      }
    }

    if (!isRowSaved) {
      const index = this.groupedFormLines.indexOf(selectedRowData);
      this.groupedFormLines.splice(index, 1);
    }

    this.cdr.detectChanges();
  }

  // P1S0: Empty calcTotalsCol
  calcTotalsCol(columnValues: FormLineItem[]): void {
  }

  onNumericChange(items: FormLineItem[]): void {
    this.cdr.detectChanges();
  }

  /**
   * Handle date input change - uses parseAsLocalDate to avoid timezone shift
   */
  onDateChange(item: FormLineItem, dateString: string): void {
    if (dateString) {
      item.CELL_VALUE = parseAsLocalDate(dateString);
    } else {
      item.CELL_VALUE = '';
    }
    this.cdr.detectChanges();
  }

  /**
   * Handle keypress for NUMERIC inputs - only allow digits and minus at start
   */
  onNumericKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;
    const selectionStart = inputElement.selectionStart || 0;

    // Allow digits 0-9
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }

    // Allow minus sign only at the start (position 0) and if not already present
    if (charStr === '-') {
      if (selectionStart === 0 && !currentValue.includes('-')) {
        return true;
      }
      return false;
    }

    // Block all other characters (including decimal point)
    return false;
  }

  /**
   * Handle paste for NUMERIC inputs - filter to only allowed characters
   */
  onNumericPaste(event: ClipboardEvent, item: FormLineItem): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    let filtered = pastedText.replace(/[^0-9-]/g, '');
    if (filtered.includes('-')) {
      const hasMinusAtStart = filtered.startsWith('-');
      filtered = filtered.replace(/-/g, '');
      if (hasMinusAtStart) {
        filtered = '-' + filtered;
      }
    }
    item.CELL_VALUE = filtered ? parseInt(filtered, 10) : null;
    this.cdr.detectChanges();
  }

  private validateForm(): boolean {
    this.isValidationError = false;
    let requiredMsgAlreadyAdded = false;

    for (const line of this.groupedFormLines) {
      line.ERR_MSG = undefined;
      requiredMsgAlreadyAdded = false;

      for (const item of line.items) {
        item.IS_ERROR = 'N';

        if (line.IS_DISPLAY === 'Y' && item.IS_ENABLED !== 'N') {
          if (item.IS_MANDATORY === 'Y' && 
              this.isEmpty(item) && 
              this.isAnyOtherValueEntered(item, line.items)) {
            this.setErrorAttribs(line, item, 'Mandatory field(s) are missing', requiredMsgAlreadyAdded);
            requiredMsgAlreadyAdded = true;
          }
        }
      }
    }

    if (this.isValidationError) {
      this.showErrCol = true;
    }

    this.cdr.detectChanges();
    return !this.isValidationError;
  }

  private isAnyOtherValueEntered(item: FormLineItem, itemList: FormLineItem[]): boolean {
    return itemList.some(x => x !== item && x.IS_ENABLED === 'Y' && this.isNotEmpty(x));
  }

  private setErrorAttribs(line: GroupedFormLine, col: FormLineItem, errMsg: string, alreadyAdded: boolean): void {
    if (!line.ERR_MSG) {
      line.ERR_MSG = '<ol></ol>';
    }
    col.IS_ERROR = 'Y';
    this.isValidationError = true;
    
    if (!alreadyAdded) {
      line.ERR_MSG = line.ERR_MSG.replace('</ol>', `<li>${errMsg}</li></ol>`);
    }
  }

  reset(): void {
    // Clear array before reload to prevent rendering stale data during async fetch
    this.groupedFormLines = [];
    this.showLoader = true;
    this.cdr.detectChanges();
    
    this.isInitialized = false;
    this.isLoading = false;
    this.loadColumnHeaders();
  }

  save(): void {
    if (this.isSaveClicked) {
      this.service.showInfo('Please wait while we save this request');
      return;
    }

    // Note: Original P1S0 has validateForm commented out, but we include it for consistency
    if (!this.validateForm()) {
      this.service.showError('Please fix all error(s)');
      return;
    }

    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      message: `Are you sure to save data for: ${this.currentEntityName}${this.currentPartnerName ? ' / ' + this.currentPartnerName : ''}`
    };
    
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  onSaveConfirm(): void {
    this.showConfirmModal = false;
    this.performSave();
  }

  onSaveCancel(): void {
    this.showConfirmModal = false;
    this.isSaveClicked = false;
    this.cdr.detectChanges();
  }

  private performSave(): void {
    this.isSaveClicked = true;

    const clobSettings: ClobSettingsObj = {
      jcd_key: this.jcdKey,
      p_form_schd: this.formSchd,
      p_code_group: this.currentCodeGroup
    };

    const dataToSave = this.buildDataToSave();

    this.service.getLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.filterParams.tax_year || '',
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        this.service.saveData(
          this.GENERAL_CONFIG.base_url,
          clobSettings,
          dataToSave,
          this.globalService?.globalParams || {},
          this.filterParams,
          this.globalService?.inputs?.tax_year?.values || []
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (result) => {
            // Use loose comparison (!=) like original AngularJS: result.data.callSuccess != 1
            if (result.callSuccess != 1) {
              // Handle errorMessage: "null" string from API
              const errorMsg = (result?.errorMessage && result.errorMessage !== 'null') 
                ? result.errorMessage 
                : 'Error saving data';
              this.service.showError(errorMsg);
            } else {
              this.service.showSuccess('Data have been saved/updated');
              this.requestGridUpdate.emit('refresh');
            }
            this.isSaveClicked = false;
            this.showErrCol = false;
            this.cdr.detectChanges();
          },
          (error) => {
            console.error('Error saving data:', error);
            this.service.showError('Error occurred while saving. Please try again.');
            this.isSaveClicked = false;
            this.cdr.detectChanges();
          }
        );
      },
      (error) => {
        console.error('Error checking lock status:', error);
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      }
    );
  }

  // P1S0 specific: Build data array for save - matching original AngularJS pushToDataToSave logic
  private buildDataToSave(): SaveDataItem[] {
    const dataToSave: SaveDataItem[] = [];

    for (const line of this.groupedFormLines) {
      for (const item of line.items) {
        const returnObj: SaveDataItem = {
          SCHD_KEY: item.SCHD_KEY,
          LINE_KEY: item.LINE_KEY,
          COL_KEY: item.COL_KEY,
          SNO: item.SNO,
          OPERATION_TYPE: null as any
        };

        // Always include ADJ_KEY (matching original AngularJS behavior)
        returnObj.ADJ_KEY = item.ADJ_KEY;

        // Handle cell value based on component type (only if IS_ENABLED != 'N')
        if (item.IS_ENABLED !== 'N') {
          if (item.UI_COMP === 'DATE') {
            if (this.isNotEmpty(item) && item.CELL_VALUE) {
              // Format date as YYYY-M-D (without leading zeros) to match original AngularJS
              returnObj.CELL_VALUE = this.formatDateForSave(item.CELL_VALUE);
            } else {
              returnObj.CELL_VALUE = '';
            }
          } else if (item.UI_COMP === 'NUMERIC') {
            // Convert to number for NUMERIC fields, empty values should be null
            if (item.CELL_VALUE !== null && item.CELL_VALUE !== undefined && item.CELL_VALUE !== '') {
              returnObj.CELL_VALUE = Number(item.CELL_VALUE);
            } else {
              returnObj.CELL_VALUE = null; // Empty NUMERIC = null (matching original)
            }
          } else if (item.UI_COMP === 'CHK_BX') {
            // Checkboxes: true → "X", false/empty → "" (matching original AngularJS)
            returnObj.CELL_VALUE = item.CELL_VALUE === true || item.CELL_VALUE === 'X' ? 'X' : '';
          } else if (item.UI_COMP === 'DROP_DOWN') {
            // Dropdowns: empty value should be null (matching original AngularJS)
            if (item.CELL_VALUE === null || item.CELL_VALUE === undefined || item.CELL_VALUE === '') {
              returnObj.CELL_VALUE = null;
            } else {
              returnObj.CELL_VALUE = item.CELL_VALUE;
            }
          } else {
            returnObj.CELL_VALUE = item.CELL_VALUE;
          }
        }

        // Determine operation type - matching original AngularJS logic exactly
        // Check if item has a real value (not null, undefined, or empty string)
        const hasValue = item.CELL_VALUE != null && item.CELL_VALUE !== '' && item.CELL_VALUE !== undefined;
        
        // FIX: Match original AngularJS pattern (P7S2, P12, P11 controllers)
        // Set OPERATION_TYPE UNCONDITIONALLY first, then handle special cases
        // This ensures items with cleared values (OPERATION_TYPE='U' but empty value) are still sent to API
        returnObj.OPERATION_TYPE = item.OPERATION_TYPE || null;

        if (line.IS_LINE_MULTI === 'Y') {
          if (!item.OPERATION_TYPE) {
            returnObj.OPERATION_TYPE = 'I';
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'I') {
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'N') {
            returnObj.OPERATION_TYPE = null;
          }
        } else {
          if (item.OPERATION_TYPE == null && hasValue) {
            // New value being inserted
            returnObj.OPERATION_TYPE = 'I';
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'I') {
            // After insert, mark as update for future saves
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'D') {
            // Deletion
            returnObj.OPERATION_TYPE = 'D';
            item.OPERATION_TYPE = undefined;
          } else if (item.OPERATION_TYPE === 'N') {
            // Disabled/not saveable
            returnObj.OPERATION_TYPE = null;
          }
          // Note: When OPERATION_TYPE is 'U', it stays 'U' (set unconditionally above)
          // This allows clearing values to be sent to API
        }

        // Only add if OPERATION_TYPE is not null
        if (returnObj.OPERATION_TYPE != null) {
          dataToSave.push(returnObj);
        }
      }
    }

    return dataToSave;
  }

  trackByLine(index: number, line: GroupedFormLine): string {
    return `${line.LINE_KEY}_${line.SNO}_${line.IS_DISPLAY}`;
  }

  trackByItem(index: number, item: FormLineItem): string {
    return `${item.LINE_KEY}_${item.COL_KEY}_${item.SNO}`;
  }

  trackByCol(index: number, col: ColumnHeaderData): number {
    return col.COL_KEY;
  }
}
